import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Autocomplete,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  getBookingGroups,
  getGroupBookingMeta,
  getStudentsStaysBatch,
  getStudentsByIds,
  linkAgentStay,
} from "./api/agentsApi";

const COMMISSION_TYPES = ["NONE", "NET", "GROSS"];
const d10 = (s) => (s ? String(s).slice(0, 10) : null);
const normCampus = (v) => String(v ?? "").trim().toLowerCase();
const rateRequired = (c) => c === "NET" || c === "GROSS";

// Bulk-link every student in a booking group to the agent in one pass. Resolves
// each student's stay by matching the group's campus + window (exact, else a
// single overlapping stay); anything ambiguous is flagged and left unticked so we
// never guess the wrong stay (which would mis-attribute commission). Reuses the
// per-stay link endpoint in a loop — no bulk backend endpoint needed.
export default function GroupConnectionDialog({ open, onClose, agent, existingStayIds, onSaved }) {
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [group, setGroup] = useState(null);

  const [rows, setRows] = useState([]);
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState(null);

  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState(null);

  const linkedSet = useMemo(
    () => new Set((existingStayIds || []).map((id) => String(id))),
    [existingStayIds]
  );

  // Defaults applied to every freshly-resolved row, seeded from the agent.
  const defCommission = String(agent?.commission || "GROSS").toUpperCase();
  const defRate = agent?.commissionRate != null ? String(agent.commissionRate) : "20";

  useEffect(() => {
    if (!open) return;
    setGroup(null);
    setRows([]);
    setError(null);
    setSummary(null);
    setGroupsLoading(true);
    getBookingGroups()
      .then((data) => setGroups(Array.isArray(data) ? data : []))
      .catch((e) => setError(e))
      .finally(() => setGroupsLoading(false));
  }, [open]);

  const resolveGroup = useCallback(
    async (g) => {
      if (!g) {
        setRows([]);
        return;
      }
      const ids = Array.isArray(g.studentIds) ? g.studentIds : [];
      setResolving(true);
      setError(null);
      setSummary(null);
      try {
        const [meta, stayRows, students] = await Promise.all([
          getGroupBookingMeta(g.id).catch(() => null),
          getStudentsStaysBatch(ids),
          getStudentsByIds(ids),
        ]);

        const campus = normCampus(meta?.defaultCampus || g.campus);
        const start = d10(meta?.defaultStartDate);
        const end = d10(meta?.defaultEndDate);

        const staysByStudent = {};
        (Array.isArray(stayRows) ? stayRows : []).forEach((r) => {
          (staysByStudent[r.studentId] = staysByStudent[r.studentId] || []).push(r);
        });
        const nameById = {};
        (Array.isArray(students) ? students : []).forEach((s) => {
          nameById[s.id] = `${s.studentName || s.name || ""} ${s.studentSurname || s.surname || ""}`.trim();
        });

        const next = ids.map((sid) => {
          const stays = staysByStudent[sid] || [];
          // Exact campus + window first; else a single overlapping stay; else flag.
          let stay =
            stays.find(
              (s) =>
                (!campus || normCampus(s.campus) === campus) &&
                d10(s.arrivalDate) === start &&
                d10(s.departureDate) === end
            ) || null;
          if (!stay) {
            const overlapping = stays.filter(
              (s) => d10(s.arrivalDate) <= end && start <= d10(s.departureDate)
            );
            if (overlapping.length === 1) stay = overlapping[0];
          }

          const name = nameById[sid] || `#${sid}`;
          if (!stay) {
            return { studentId: sid, name, stayId: null, status: "no-stay", selected: false };
          }
          if (linkedSet.has(String(stay.stayId))) {
            return {
              studentId: sid,
              name,
              stayId: stay.stayId,
              stayText: `${stay.arrivalDate} – ${stay.departureDate}`,
              status: "linked",
              selected: false,
            };
          }
          return {
            studentId: sid,
            name,
            stayId: stay.stayId,
            stayText: `${stay.arrivalDate} – ${stay.departureDate}`,
            campus: stay.campus,
            status: "ok",
            selected: true,
            commission: defCommission,
            commissionRate: defRate,
          };
        });
        setRows(next);
      } catch (e) {
        setError(e);
        setRows([]);
      } finally {
        setResolving(false);
      }
    },
    [linkedSet, defCommission, defRate]
  );

  const onPickGroup = (g) => {
    setGroup(g);
    resolveGroup(g);
  };

  const patchRow = (studentId, patch) =>
    setRows((prev) => prev.map((r) => (r.studentId === studentId ? { ...r, ...patch } : r)));

  const setAll = (patch) =>
    setRows((prev) => prev.map((r) => (r.status === "ok" ? { ...r, ...patch } : r)));

  const selectable = rows.filter((r) => r.status === "ok");
  const selectedRows = selectable.filter((r) => r.selected);

  const rowValid = (r) => {
    if (!rateRequired(r.commission)) return true;
    const n = Number(r.commissionRate);
    return Number.isFinite(n) && n > 0;
  };
  const canLink = selectedRows.length > 0 && selectedRows.every(rowValid) && !saving;

  const handleLink = async () => {
    if (!canLink || !agent?.id) return;
    setSaving(true);
    setError(null);
    const results = { linked: 0, alreadyLinked: 0, failed: [] };
    const doneStayIds = new Set();
    // Sequential — small N, and it keeps backend writes predictable.
    for (const r of selectedRows) {
      try {
        await linkAgentStay(agent.id, r.stayId, {
          commission: r.commission,
          commissionRate: rateRequired(r.commission) ? Number(r.commissionRate) : 0,
        });
        results.linked += 1;
        doneStayIds.add(r.stayId);
      } catch (e) {
        // 409 = the stay is already linked to this agent. Treat as a no-op skip,
        // not a failure — the desired end-state (linked) is true either way.
        if (e.status === 409) {
          results.alreadyLinked += 1;
          doneStayIds.add(r.stayId);
        } else {
          results.failed.push({ name: r.name, message: String(e.message || e) });
        }
      }
    }
    setSaving(false);
    // Flip every now-linked row to "linked" + untick, so a second click can't
    // re-send them (which is exactly what produced the 409).
    setRows((prev) =>
      prev.map((row) =>
        doneStayIds.has(row.stayId) ? { ...row, status: "linked", selected: false } : row
      )
    );
    setSummary(results);
    if (results.linked > 0 || results.alreadyLinked > 0) await onSaved?.();
  };

  const statusChip = (r) => {
    if (r.status === "linked")
      return <Chip size="small" label="Already linked" variant="outlined" />;
    if (r.status === "no-stay")
      return <Chip size="small" color="warning" label="No matching stay" variant="outlined" />;
    return null;
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Add a booking group — {agent?.name || `Agent #${agent?.id}`}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error ? <Alert severity="error">{String(error.message || error)}</Alert> : null}

          <Autocomplete
            options={groups}
            loading={groupsLoading}
            value={group}
            onChange={(_, v) => onPickGroup(v)}
            getOptionLabel={(g) =>
              `${g.groupName || `Group #${g.id}`} (${(g.studentIds || []).length} students${
                g.campus ? `, ${g.campus}` : ""
              })`
            }
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={(params) => (
              <TextField {...params} label="Booking group" size="small" />
            )}
          />

          {group && rows.some((r) => r.status === "ok") && (
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                Set all selected:
              </Typography>
              {COMMISSION_TYPES.map((t) => (
                <Button key={t} size="small" variant="outlined" onClick={() => setAll({ commission: t })}>
                  {t === "NONE" ? "None" : t}
                </Button>
              ))}
              <TextField
                label="Rate % (all)"
                size="small"
                type="number"
                sx={{ width: 130 }}
                onChange={(e) => setAll({ commissionRate: e.target.value })}
                inputProps={{ min: 0, step: 0.5 }}
              />
            </Stack>
          )}

          {resolving ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} />
              <Typography variant="body2">Resolving stays…</Typography>
            </Stack>
          ) : group && rows.length === 0 ? (
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              This group has no students.
            </Typography>
          ) : rows.length > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selectable.length > 0 && selectedRows.length === selectable.length}
                      indeterminate={selectedRows.length > 0 && selectedRows.length < selectable.length}
                      onChange={(e) => setAll({ selected: e.target.checked })}
                    />
                  </TableCell>
                  <TableCell>Student</TableCell>
                  <TableCell>Stay</TableCell>
                  <TableCell>Commission</TableCell>
                  <TableCell>Rate %</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => {
                  const disabled = r.status !== "ok";
                  return (
                    <TableRow key={r.studentId} sx={disabled ? { opacity: 0.6 } : undefined}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={!!r.selected}
                          disabled={disabled}
                          onChange={(e) => patchRow(r.studentId, { selected: e.target.checked })}
                        />
                      </TableCell>
                      <TableCell>{r.name}</TableCell>
                      <TableCell>{r.stayText || statusChip(r)}</TableCell>
                      <TableCell>
                        {disabled ? (
                          statusChip(r)
                        ) : (
                          <FormControl size="small" sx={{ minWidth: 110 }}>
                            <Select
                              value={r.commission}
                              onChange={(e) => patchRow(r.studentId, { commission: e.target.value })}
                            >
                              {COMMISSION_TYPES.map((t) => (
                                <MenuItem key={t} value={t}>
                                  {t === "NONE" ? "None" : t}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      </TableCell>
                      <TableCell>
                        {!disabled && rateRequired(r.commission) ? (
                          <TextField
                            size="small"
                            type="number"
                            sx={{ width: 90 }}
                            value={r.commissionRate}
                            error={!rowValid(r)}
                            onChange={(e) => patchRow(r.studentId, { commissionRate: e.target.value })}
                            inputProps={{ min: 0, step: 0.5 }}
                          />
                        ) : (
                          "—"
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : null}

          {summary ? (
            <Alert severity={summary.failed.length ? "warning" : "success"}>
              Linked {summary.linked} stay{summary.linked === 1 ? "" : "s"}.
              {summary.alreadyLinked
                ? ` ${summary.alreadyLinked} already linked (skipped).`
                : ""}
              {summary.failed.length
                ? ` ${summary.failed.length} failed: ${summary.failed
                    .map((f) => `${f.name} (${f.message})`)
                    .join("; ")}`
                : ""}
            </Alert>
          ) : null}

          {rows.some((r) => r.status === "no-stay") ? (
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              "No matching stay" rows have no stay that cleanly matches the group's campus + dates —
              add those individually so the right stay is linked.
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {summary ? "Close" : "Cancel"}
        </Button>
        <Button variant="contained" onClick={handleLink} disabled={!canLink}>
          {saving ? "Linking…" : `Link ${selectedRows.length} selected`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
